/**
 * Command context utilities
 *
 * Helper functions for working with CommandContext safely,
 * providing default values when context is undefined or missing properties.
 */

import type { CommandContext, SessionNode } from '../types/commands'

/**
 * Default values for command context
 */
export const CONTEXT_DEFAULTS = {
  /** Default current directory (home) */
  currentDirectory: '~',
  /** Default session filesystem (empty array) */
  sessionFilesystem: [] as SessionNode[],
} as const

/**
 * Get the current directory from context with default fallback
 *
 * @param context - Command context (may be undefined)
 * @returns Current directory or default '~'
 *
 * @example
 * ```typescript
 * const currentDir = getCurrentDirectory(context)
 * // Returns context?.currentDirectory || '~'
 * ```
 */
export function getCurrentDirectory(context?: CommandContext): string {
  return context?.currentDirectory || CONTEXT_DEFAULTS.currentDirectory
}

/**
 * Get the session filesystem from context with default fallback
 *
 * @param context - Command context (may be undefined)
 * @returns Session filesystem or default empty array
 *
 * @example
 * ```typescript
 * const sessionFs = getSessionFilesystem(context)
 * // Returns context?.sessionFilesystem || []
 * ```
 */
export function getSessionFilesystem(context?: CommandContext): SessionNode[] {
  return context?.sessionFilesystem || CONTEXT_DEFAULTS.sessionFilesystem
}

/**
 * Get both current directory and session filesystem from context
 *
 * This is a convenience function for commands that need both values,
 * reducing repetitive destructuring code.
 *
 * @param context - Command context (may be undefined)
 * @returns Object with currentDirectory and sessionFilesystem
 *
 * @example
 * ```typescript
 * // Before:
 * const currentDirectory = context?.currentDirectory || '~'
 * const sessionFs = context?.sessionFilesystem || []
 *
 * // After:
 * const { currentDirectory, sessionFs } = getContextDefaults(context)
 * ```
 */
export function getContextDefaults(context?: CommandContext): {
  currentDirectory: string
  sessionFs: SessionNode[]
} {
  return {
    currentDirectory: getCurrentDirectory(context),
    sessionFs: getSessionFilesystem(context),
  }
}

/**
 * Check if context has all required properties
 *
 * Useful for defensive programming and debugging.
 *
 * @param context - Command context to validate
 * @returns true if context is fully defined
 *
 * @example
 * ```typescript
 * if (!isContextComplete(context)) {
 *   console.warn('Context is incomplete, using defaults')
 * }
 * ```
 */
export function isContextComplete(context?: CommandContext): context is Required<CommandContext> {
  return Boolean(
    context &&
    context.currentDirectory != null &&
    context.sessionFilesystem != null &&
    typeof context.setCurrentDirectory === 'function' &&
    typeof context.setSessionFilesystem === 'function'
  )
}
